/**
 * TON-wallet auth for the partner panel, via TON Connect `ton_proof`.
 *
 * The partner panel needs a SIGNED session, so connecting and signing must be
 * a single step: while no wallet is connected we keep the connect request armed
 * with a fresh proof payload, so opening the connect modal already asks the
 * wallet to sign. When the wallet returns a signed proof we exchange it for a
 * session token (kept in localStorage, keyed by address). `login()` is the one
 * entry point used by every CTA: it (re)connects requesting a proof, so even a
 * proof-less session carried over from the main app is upgraded in one action
 * instead of a second "verify" step.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTonAddress, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { PARTNER_API_AVAILABLE, fetchProofPayload, verifyProof } from './api';
import {
  readStoredSession,
  readToken,
  removeToken,
  revokePartnerSession,
  tokenExpired,
  tokenExpiryMs,
  writeToken,
} from './session';
import {
  disconnectForReauthentication,
  subscribeToTonConnectUiDisconnection,
} from './tonConnectDisconnection';

export interface PartnerAuth {
  /** Connected wallet address (user-friendly form), or ''. */
  address: string;
  /** Session token for the partner API, or null when not verified. */
  token: string | null;
  verifying: boolean;
  error: string | null;
  /**
   * True when a session that DID exist was rejected by the API (401 —
   * expired/revoked server-side). Distinguishes "session expired, sign in
   * again" from a brand-new visitor who simply never signed in.
   */
  expired: boolean;
  /**
   * Single connect-and-sign entry point: opens the wallet requesting a
   * ton_proof (disconnecting first if a proof-less wallet is already attached),
   * so one action yields a signed partner session.
   * Pass `remember` to issue a longer-lived token persisted across browser
   * restarts on this device (default: a short session cleared on tab close).
   */
  login: (remember?: boolean) => Promise<void>;
  /** Drops the local session token (e.g. after a 401 from the API). */
  clearToken: () => void;
}

export function usePartnerAuth(remember = false): PartnerAuth {
  const walletAddress = useTonAddress();
  const wallet = useTonWallet();
  const [tonConnectUI] = useTonConnectUI();
  const [sessionAddress, setSessionAddress] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const address = sessionAddress;
  // Carries the "remember this device" choice into the async proof-verification
  // effect below. Seeded from the caller's current checkbox value so EVERY
  // connect path honors it (the hero CTA, the header wallet button, and the
  // verify panel) — not only an explicit login(remember) call.
  const rememberRef = useRef(remember);
  useEffect(() => {
    rememberRef.current = remember;
  }, [remember]);

  // Restore a stored session for the connected address. A wallet change also
  // clears any "expired" flag from the previous wallet.
  useEffect(() => {
    setExpired(false);
    if (walletAddress) {
      setSessionAddress(walletAddress);
      setToken(readToken(walletAddress));
      return;
    }
    const stored = readStoredSession();
    setSessionAddress(stored?.address ?? '');
    setToken(stored?.token ?? null);
  }, [walletAddress]);

  // Only the TonConnect UI event represents the user pressing Disconnect in
  // this dapp. Core SDK wallet disconnects can also be emitted during failed
  // restoration and must not revoke a valid partner session.
  useEffect(() => {
    return subscribeToTonConnectUiDisconnection((disconnectedAddress) => {
      revokePartnerSession(disconnectedAddress);
      setSessionAddress('');
      setToken(null);
    });
  }, []);

  // Enforce the server-issued stateless token expiry client-side too. Without a
  // timer, an already-open dashboard would keep looking signed in until the next
  // API call returned 401.
  useEffect(() => {
    if (!address || !token) return;
    const expiresAt = tokenExpiryMs(token);
    const expire = () => {
      removeToken(address);
      setToken(null);
      setExpired(true);
    };
    if (expiresAt === null || expiresAt <= Date.now()) {
      expire();
      return;
    }

    let timeout: number | undefined;
    let cancelled = false;
    const schedule = () => {
      if (cancelled) return;
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) {
        expire();
        return;
      }
      timeout = window.setTimeout(schedule, Math.min(remaining, 60 * 60 * 1000));
    };
    const check = () => {
      if (tokenExpired(token)) expire();
    };

    schedule();
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      cancelled = true;
      if (timeout !== undefined) window.clearTimeout(timeout);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, [address, token]);

  // Attach a fresh proof payload to the NEXT connect request (no-op while a
  // wallet is already connected — TON Connect only signs proofs on connect).
  const armProofRequest = useCallback(async () => {
    if (!PARTNER_API_AVAILABLE) return;
    tonConnectUI.setConnectRequestParameters({ state: 'loading' });
    try {
      const payload = await fetchProofPayload();
      tonConnectUI.setConnectRequestParameters({
        state: 'ready',
        value: { tonProof: payload },
      });
    } catch {
      // Backend unreachable — allow a plain connect rather than blocking it.
      tonConnectUI.setConnectRequestParameters(null);
    }
  }, [tonConnectUI]);

  // Keep the connect request armed with a proof payload the whole time no
  // wallet is connected (on mount and re-armed after every disconnect), so the
  // very first connect the visitor makes already signs — no separate step.
  useEffect(() => {
    if (wallet) return;
    void armProofRequest();
  }, [wallet, armProofRequest]);

  // When the wallet connects with a signed proof, exchange it for a token.
  useEffect(() => {
    const item = wallet?.connectItems?.tonProof;
    if (!wallet || !item || !('proof' in item)) return;
    const account = wallet.account;
    const friendly = walletAddress;
    if (!friendly || !account.publicKey || !account.walletStateInit) return;
    if (readToken(friendly)) return; // already verified
    setVerifying(true);
    setError(null);
    const remember = rememberRef.current;
    verifyProof(
      {
        address: account.address,
        publicKey: account.publicKey,
        walletStateInit: account.walletStateInit,
        proof: item.proof,
      },
      remember,
    )
      .then((t) => {
        writeToken(friendly, t, remember);
        setToken(t);
        setExpired(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'verification failed');
      })
      .finally(() => setVerifying(false));
  }, [wallet, walletAddress]);

  // One connect-and-sign action for every CTA. A wallet already connected
  // without a proof (e.g. restored from a main-app session) can't be asked to
  // sign in place — TON Connect only signs a proof on connect — so we drop it
  // first, then reconnect requesting the proof. The wallet shows a single
  // approval that both connects and signs.
  const login = useCallback(
    async (rememberArg?: boolean) => {
      setError(null);
      setExpired(false);
      if (typeof rememberArg === 'boolean') rememberRef.current = rememberArg;
      if (wallet) {
        await disconnectForReauthentication(() =>
          tonConnectUI.disconnect().catch(() => undefined),
        );
      }
      await armProofRequest();
      await tonConnectUI.openModal();
    },
    [wallet, tonConnectUI, armProofRequest],
  );

  // Called on a 401 from the partner API: the token we held is no longer
  // accepted (expired/revoked server-side). Drop it and flag the expiry so the
  // UI can prompt a fresh sign-in rather than showing the newcomer landing.
  const clearToken = useCallback(() => {
    if (address) removeToken(address);
    setSessionAddress('');
    setToken(null);
    setExpired(true);
  }, [address]);

  return { address, token, verifying, error, expired, login, clearToken };
}
